import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Book,
  BookOpen,
  FileText,
  Image as ImageIcon,
  Loader2,
  Save,
} from "lucide-react";
import AdminNavBar from "../components/AdminNavBar";
import { AdminPageBody, AdminLoadingState, AdminEmptyState } from "../components/AdminPageShell";
import { useAdminAcademicResources } from "../store/admin";

const KIND_META = {
  book: { plural: "books", label: "Book", icon: Book },
  course: { plural: "courses", label: "Course", icon: BookOpen },
  journal: { plural: "journals", label: "Journal", icon: FileText },
};

const inputClass =
  "mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#004ac6] focus:ring-2 focus:ring-[#004ac6]/15";
const labelClass = "block text-xs font-semibold uppercase tracking-wide text-slate-500";

const toForm = (detail = {}) => ({
  title: detail.title || "",
  author: detail.author || "",
  description: detail.description || "",
  price: detail.price ?? "",
  coverImage: detail.coverImage || detail.cover_image || "",
  fileUrl: detail.fileUrl || detail.file_url || "",
  categoryId: detail.categoryId || detail.category?.id || "",
});

// ===== EDIT ACADEMIC RESOURCE =====
// A dedicated page keeps the review screen read-only while giving the editor
// room to work. Saving redirects back to the resource detail page.
const AcademicResourceEdit = () => {
  const { kind, resourceId } = useParams();
  const navigate = useNavigate();
  const meta = KIND_META[kind];
  const plural = meta?.plural;

  const {
    bookDetail,
    bookDetailLoading,
    bookDetailError,
    fetchBook,
    courseDetail,
    courseDetailLoading,
    courseDetailError,
    fetchCourse,
    journalDetail,
    journalDetailLoading,
    journalDetailError,
    fetchJournal,
    updateBook,
    updateCourse,
    updateJournal,
    academicMutationLoading,
    academicMutationError,
  } = useAdminAcademicResources();

  const config = {
    books: { detail: bookDetail, loading: bookDetailLoading, error: bookDetailError, fetch: fetchBook, update: updateBook },
    courses: { detail: courseDetail, loading: courseDetailLoading, error: courseDetailError, fetch: fetchCourse, update: updateCourse },
    journals: { detail: journalDetail, loading: journalDetailLoading, error: journalDetailError, fetch: fetchJournal, update: updateJournal },
  }[plural] || {};

  const [form, setForm] = useState(toForm());
  const [loadedFor, setLoadedFor] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!config.fetch || !resourceId) return;
    let active = true;
    config.fetch(resourceId).then((result) => {
      if (!active || !result?.success) return;
      setForm(toForm(result.data));
      setLoadedFor(resourceId);
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plural, resourceId]);

  const set = (key) => (event) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (!form.title.trim()) {
      setError("A title is required.");
      return;
    }

    const allowed = {
      books: ["title", "author", "price", "coverImage", "fileUrl"],
      courses: ["title", "description", "price", "coverImage", "categoryId"],
      journals: ["title", "author", "price", "fileUrl"],
    }[plural] || [];

    const payload = {};
    allowed.forEach((key) => {
      const value = form[key];
      if (value === undefined || value === null || value === "") return;
      if (key === "price") {
        const price = Number(value);
        if (!Number.isNaN(price)) payload.price = price;
        return;
      }
      payload[key] = typeof value === "string" ? value.trim() : value;
    });

    const result = await config.update(resourceId, payload);
    if (!result?.success) {
      setError(result?.error || "Could not save these changes.");
      return;
    }
    navigate(`/admin/resources/${kind}/${resourceId}`);
  };

  const goBack = () => navigate(`/admin/resources/${kind}/${resourceId}`);
  const detail = config.detail;
  const TypeIcon = meta?.icon || BookOpen;
  const coverPreview = form.coverImage || detail?.coverImage || detail?.cover_image || "";

  return (
    <div>
      <AdminNavBar
        title="Academic Resources"
        subTitle={detail?.title || "Edit"}
        context={`Update this ${meta?.label?.toLowerCase() || "resource"}'s details.`}
      />
      <AdminPageBody>
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-medium text-slate-600 shadow-sm transition hover:bg-slate-50"
        >
          <ArrowLeft className="h-4 w-4" /> Back to details
        </button>

        {!meta ? (
          <AdminEmptyState title="Unknown resource type" description={`No editor for "${kind}".`} />
        ) : config.loading || (!loadedFor && !config.error) ? (
          <AdminLoadingState />
        ) : config.error && !detail ? (
          <AdminEmptyState title={`${meta.label} not found`} description={config.error} />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Header */}
            <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#004ac6]/10 text-[#004ac6]">
                <TypeIcon className="h-6 w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-[#004ac6]">
                  Edit {meta.label}
                </p>
                <h1 className="m-0 mt-1 truncate text-xl font-bold text-slate-900">
                  {detail?.title || `New ${meta.label.toLowerCase()}`}
                </h1>
              </div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                {detail?.status || "—"}
              </span>
            </div>

            {(error || academicMutationError) && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error || academicMutationError}
              </div>
            )}

            {/* Basic details */}
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-5">
                <h2 className="m-0 text-base font-semibold text-slate-900">Basic details</h2>
                <p className="m-0 mt-1 text-sm text-slate-500">
                  The title and description learners see first.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className={`${labelClass} sm:col-span-2`}>
                  Title
                  <input
                    className={inputClass}
                    value={form.title}
                    onChange={set("title")}
                    placeholder={`e.g. ${meta.label} title`}
                  />
                </label>

                {plural === "courses" ? (
                  <label className={`${labelClass} sm:col-span-2`}>
                    Description
                    <textarea
                      rows={5}
                      className={`${inputClass} resize-y font-mono text-[13px] leading-relaxed`}
                      value={form.description}
                      onChange={set("description")}
                      placeholder="Describe what this course covers. Basic HTML is supported."
                    />
                    <span className="mt-1.5 block text-[11px] font-normal normal-case tracking-normal text-slate-400">
                      Basic HTML is supported.
                    </span>
                  </label>
                ) : (
                  <label className={labelClass}>
                    Author
                    <input
                      className={inputClass}
                      value={form.author}
                      onChange={set("author")}
                      placeholder="Author name"
                    />
                  </label>
                )}
              </div>
            </section>

            {/* Pricing & media */}
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-5">
                <h2 className="m-0 text-base font-semibold text-slate-900">Pricing & media</h2>
                <p className="m-0 mt-1 text-sm text-slate-500">
                  Set the price and where the cover or file lives.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className={labelClass}>
                  Price
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">
                      ₦
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className={`${inputClass} pl-7`}
                      value={form.price}
                      onChange={set("price")}
                      placeholder="0.00"
                    />
                  </div>
                </label>

                {plural === "courses" ? (
                  <label className={labelClass}>
                    Category ID
                    <input
                      className={inputClass}
                      value={form.categoryId}
                      onChange={set("categoryId")}
                      placeholder="Category UUID"
                    />
                  </label>
                ) : (
                  <label className={labelClass}>
                    File URL
                    <input
                      className={inputClass}
                      value={form.fileUrl}
                      onChange={set("fileUrl")}
                      placeholder="https://…"
                    />
                  </label>
                )}

                <label className={`${labelClass} sm:col-span-2`}>
                  Cover image URL
                  <input
                    className={inputClass}
                    value={form.coverImage}
                    onChange={set("coverImage")}
                    placeholder="https://…"
                  />
                </label>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex h-20 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white">
                  {coverPreview ? (
                    <img src={coverPreview} alt="Cover preview" className="h-full w-full object-cover" />
                  ) : (
                    <ImageIcon className="h-6 w-6 text-slate-400" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="m-0 text-sm font-semibold text-slate-800">Cover preview</p>
                  <p className="m-0 mt-0.5 truncate text-xs text-slate-500">
                    {coverPreview || "Paste an image URL to preview it here."}
                  </p>
                </div>
              </div>
            </section>

            <div className="flex flex-wrap justify-end gap-3 pb-4">
              <button
                type="button"
                onClick={goBack}
                className="inline-flex min-h-11 items-center justify-center rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={academicMutationLoading}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#004ac6] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#003ba0] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {academicMutationLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Saving…
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" /> Save changes
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </AdminPageBody>
    </div>
  );
};

export default AcademicResourceEdit;
